import React, { useEffect, useState } from 'react';
import Footer from '../component/Footer/FooterCommon';
import Navbar from '../component/Navbar/NavbarCommon';
import ArrowButton from '../component/Common/ArrowButton';
import { api } from '../api';
import { API_URL, ASSETS_URL } from '../utils/config';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Link } from 'react-router-dom';

interface Blog {
  _id: string;
  title: string;
  excerpt: string;
  content: string;
  coverImage: string;
  category: string;
  tags: string[];
  status: string;
  readingTime: number;
  featured: boolean;
  publishedAt: string;
  createdAt: string;
  author?: {
    name: string;
    avatar: string;
  };
}

const BlogsList = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const { data, error } = await api.get(`${API_URL.blogsPublished}`);
      if (error) {
        toast.error(error.message);
        return;
      }
      if (data) {
        const blogsData = (data as any)?.data?.blogs || [];
        // Sort by publishedAt (latest first)
        const sortedBlogs = blogsData.sort((a: Blog, b: Blog) =>
          new Date(b.publishedAt || b.createdAt).getTime() - new Date(a.publishedAt || a.createdAt).getTime()
        );
        setBlogs(sortedBlogs);
      }
    } catch (err) {
      toast.error('Failed to fetch blogs');
    } finally {
      setLoading(false);
    }
  };

  const stripHtml = (html: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  return (
    <div className="min-h-screen bg-gray-50 font-poppins relative overflow-hidden">
      <Navbar />

      {/* Blue gradient effect at bottom */}
      <div className="fixed bottom-0 left-0 right-0 h-40 pointer-events-none z-0">
        <div className="w-full h-full bg-gradient-to-t from-blue-100/60 via-blue-50/40 to-transparent"></div>
      </div>

      {/* Main Content */}
      <div className="flex-grow py-4 lg:py-6 px-4 lg:px-10 mt-20 md:mt-2 lg:mt-28 xl:mt-28 font-poppins overflow-x-hidden">
        <ArrowButton />

        {/* Header Section */}
        <div className="max-w-full mx-auto mb-8 sm:mb-12 md:mb-16 lg:mb-5 xl:mb-10 lg:mt-5 xl:mt-10">
          <h1 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-semibold mb-4">
            <span className="text-navgreen">Our </span>
            <span className="text-black">Blogs</span>
          </h1>
          <p className="text-sm md:text-base lg:text-lg text-deepTeal font-medium max-w-2xl">
            Discover insights, updates, and stories from our team.
          </p>
        </div>

        {/* Featured Blog (First/Latest) */}
        {/* {blogs.length > 0 && blogs[0]?.coverImage && (
          <div className="max-w-full mx-auto mb-8 md:mb-10">
            <Link to={`/blogs/${blogs[0]._id}`}>
              <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl h-48 md:h-64 lg:h-80 xl:h-96 group cursor-pointer">
                <div className="absolute inset-0">
                  <img
                    src={`${ASSETS_URL}${blogs[0].coverImage}`}
                    alt={blogs[0].title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                <div className="absolute bottom-4 md:bottom-6 lg:bottom-8 left-4 md:left-6 lg:left-8 right-4 md:right-6 lg:right-8 z-20">
                  {blogs[0].category && (
                    <span className="inline-block px-3 py-1 bg-navgreen text-white text-xs sm:text-sm rounded-full mb-2">
                      {blogs[0].category}
                    </span>
                  )}
                  <h2 className="text-white text-lg md:text-xl lg:text-2xl xl:text-3xl font-semibold drop-shadow-lg mb-2">
                    {blogs[0].title}
                  </h2>
                  <div className="flex items-center gap-3 text-white/80 text-sm">
                    <span>{moment(blogs[0].publishedAt || blogs[0].createdAt).format('MMMM D, YYYY')}</span>
                    <span>|</span>
                    <span>{blogs[0].readingTime || 1} min read</span>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        )} */}

        {/* Blogs List - Horizontal Cards */}
        <div className="max-w-full mx-auto mb-16">
          <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-deepTeal mb-6">
            Latest Articles
          </h2>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-gray-500 text-lg">Loading blogs...</div>
            </div>
          ) : blogs.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-gray-500 text-lg">No blogs available yet.</div>
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              {blogs.map((blog) => (
                <Link to={`/blogs/${blog._id}`} key={blog._id}>
                  <div className="bg-white rounded-2xl sm:rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden group cursor-pointer mb-4">
                    <div className="flex flex-col sm:flex-row">
                      {/* Cover Image - Left Side */}
                      <div className="sm:w-48 md:w-56 lg:w-64 xl:w-72 flex-shrink-0">
                        <div className="h-48 sm:h-full relative overflow-hidden">
                          {blog.coverImage ? (
                            <img
                              src={`${ASSETS_URL}${blog.coverImage}`}
                              alt={blog.title}
                              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 "
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-navgreen/20 to-navgreen/40 flex items-center justify-center">
                              <span className="text-4xl">📝</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Content - Right Side */}
                      <div className="flex-1 p-4 sm:p-6 flex flex-col justify-between">
                        <div>
                          {/* Category & Date Row */}
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                            {blog.category && (
                              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs sm:text-sm rounded-full font-medium">
                                {blog.category}
                              </span>
                            )}
                            <span className="text-gray-500 text-xs sm:text-sm">
                              {moment(blog.publishedAt || blog.createdAt).format('MMMM D, YYYY')}
                            </span>
                            <span className="text-gray-400 hidden sm:inline">|</span>
                            <span className="text-gray-500 text-xs sm:text-sm">
                              {blog.readingTime || 1} min read
                            </span>
                          </div>

                          {/* Title */}
                          <h3 className="text-base sm:text-lg md:text-xl font-semibold text-deepTeal mb-2 line-clamp-2 group-hover:text-navgreen transition-colors">
                            {blog.title}
                          </h3>

                          {/* Description/Excerpt */}
                          <p className="text-sm sm:text-base text-gray-600 leading-relaxed line-clamp-2 sm:line-clamp-3">
                            {blog.excerpt || stripHtml(blog.content).slice(0, 150)}...
                          </p>
                        </div>

                        {/* Read More */}
                        <div className="flex items-center justify-between mt-3 sm:mt-4 pt-3 border-t border-gray-100">
                          <div className="flex items-center gap-2">
                            {blog.author?.name && (
                              <span className="text-xs sm:text-sm text-gray-500">
                                By {blog.author.name}
                              </span>
                            )}
                          </div>
                          <button className="flex items-center gap-1 sm:gap-2 text-navgreen font-medium text-sm sm:text-base transition-colors group">
                            <span>Read Article</span>
                            <img
                              src="assets/arrow-assistant.svg"
                              alt="arrow"
                              className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform"
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlogsList;
